import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  FlaskConical,
  KeyRound,
  ShieldCheck,
  UserRoundSearch,
} from "lucide-react"
import { useState } from "react"

import { DemoNotice } from "@/components/demo-notice"
import { ServiceBrand } from "@/components/service-brand"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { DEMO_PASSWORD, viewerDefaultPath } from "@/lib/domain"
import type { DemoAccount } from "@/lib/domain"
import { publicQueryOptions } from "@/lib/query-options"
import { cn } from "@/lib/utils"
import { loginFn } from "@/server/functions"

export const Route = createFileRoute("/login")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(publicQueryOptions()),
  head: () => ({
    meta: [{ title: "Demo sign in · Service Portal" }],
  }),
  component: LoginPage,
})

function LoginPage() {
  const { data } = useSuspenseQuery(publicQueryOptions())
  const { accounts, viewer } = data
  const [email, setEmail] = useState(accounts.at(0)?.email ?? "")
  const [password, setPassword] = useState(DEMO_PASSWORD)
  const [showPassword, setShowPassword] = useState(false)
  const loginMutation = useMutation({
    mutationFn: () => loginFn({ data: { email, password } }),
    onSuccess: (nextViewer) => {
      window.location.assign(viewerDefaultPath(nextViewer))
    },
  })

  function selectAccount(account: Omit<DemoAccount, "password">) {
    setEmail(account.email)
    setPassword(DEMO_PASSWORD)
    loginMutation.reset()
  }

  return (
    <div className="min-h-svh bg-[var(--navy)]">
      <DemoNotice />
      <div className="grid min-h-[calc(100svh-40px)] lg:grid-cols-[0.9fr_1.1fr]">
        <section className="relative hidden overflow-hidden border-r border-white/10 px-10 py-10 text-white lg:flex lg:flex-col">
          <div className="absolute inset-0 opacity-35">
            <svg viewBox="0 0 800 900" className="size-full" aria-hidden="true">
              <path
                d="M-50 690C140 478 205 187 693 73"
                fill="none"
                stroke="#22b6cf"
                strokeWidth="1.5"
                strokeDasharray="8 9"
              />
              <path
                d="M-84 768C191 498 298 236 820 115"
                fill="none"
                stroke="#ffffff"
                strokeOpacity=".26"
                strokeWidth="1"
              />
              <circle cx="589" cy="99" r="7" fill="#22b6cf" />
              <circle cx="203" cy="474" r="4" fill="#f2bd3d" />
              <path
                d="M394 156c51 56 69 113 54 172-12 48 7 82 49 115 36 29 47 68 21 117-20 37-13 86 21 147"
                fill="none"
                stroke="#ffffff"
                strokeOpacity=".16"
                strokeWidth="64"
              />
            </svg>
          </div>
          <ServiceBrand inverted />
          <div className="relative z-10 my-auto max-w-[580px]">
            <p className="flex items-center gap-2 text-xs font-semibold text-[var(--cyan)]">
              <FlaskConical className="size-4" />
              Safe portfolio access
            </p>
            <h1 className="mt-5 max-w-[12ch] text-[clamp(3.2rem,6vw,5.6rem)] leading-[0.92] font-semibold tracking-[-0.04em]">
              Two roles. One traceable request.
            </h1>
            <p className="mt-6 max-w-[52ch] text-base leading-7 text-white/65">
              Start in the analyst workspace to search coverage and submit a
              synthetic imagery request. Switch to the administrator account to
              inspect the operational queue and update its state.
            </p>
            <div className="mt-8 grid gap-3 text-sm text-white/72">
              {[
                "Sealed seven-day demo session",
                "No production account or service connection",
                "Resettable synthetic orders and downloads",
              ].map((item) => (
                <p key={item} className="flex items-center gap-2">
                  <span className="grid size-5 place-items-center rounded-[6px] bg-white/8 text-[var(--cyan)]">
                    <Check className="size-3" />
                  </span>
                  {item}
                </p>
              ))}
            </div>
          </div>
          <p className="relative z-10 text-[0.68rem] text-white/62">
            Service Portal portfolio demo · synthetic identities only
          </p>
        </section>

        <main className="flex min-h-full flex-col bg-[var(--cloud)]">
          <div className="flex min-h-[78px] items-center justify-between border-b border-border bg-white px-4 sm:px-8 lg:px-10">
            <div className="lg:hidden">
              <ServiceBrand />
            </div>
            <Button
              render={<Link to="/" />}
              variant="ghost"
              className="ml-auto"
            >
              <ArrowLeft />
              Back to overview
            </Button>
          </div>

          <div className="flex flex-1 items-center justify-center px-4 py-10 sm:px-8 lg:px-12">
            <div className="w-full max-w-[620px]">
              <p className="text-xs font-semibold text-[var(--service-blue)]">
                Demo access
              </p>
              <h2 className="mt-2 text-[clamp(2.2rem,5vw,3.7rem)] leading-[1] font-semibold tracking-[-0.035em] text-[var(--navy)]">
                Sign in to the portal
              </h2>
              <p className="mt-4 max-w-[58ch] text-sm leading-6 text-muted-foreground">
                Choose either demonstration identity. Credentials are visible by
                design and are not accepted by any production system.
              </p>

              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                {accounts.map((account) => {
                  const isSelected = email === account.email
                  const AccountIcon =
                    account.role === "admin" ? ShieldCheck : UserRoundSearch
                  return (
                    <button
                      key={account.id}
                      type="button"
                      onClick={() => selectAccount(account)}
                      className={cn(
                        "group min-h-[126px] rounded-[14px] border p-4 text-left transition-[background-color,border-color,box-shadow] focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[var(--cyan)]",
                        isSelected
                          ? "border-[var(--service-blue)] bg-white shadow-[0_12px_32px_-24px_rgba(18,58,90,.7)]"
                          : "border-border bg-white/55 hover:border-[#a8bbc3] hover:bg-white"
                      )}
                      aria-pressed={isSelected}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span
                          className={cn(
                            "grid size-10 place-items-center rounded-[10px]",
                            isSelected
                              ? "bg-[var(--navy)] text-[var(--cyan)]"
                              : "bg-[#e8eef0] text-[var(--service-blue)]"
                          )}
                        >
                          <AccountIcon className="size-[1.05rem]" />
                        </span>
                        <span
                          className={cn(
                            "mt-1 size-4 rounded-full border-2",
                            isSelected
                              ? "border-white bg-[var(--action)] shadow-[0_0_0_1px_var(--action)]"
                              : "border-[#afbec4]"
                          )}
                        />
                      </div>
                      <p className="mt-4 text-sm font-semibold text-[var(--navy)]">
                        {account.role === "admin"
                          ? "Administrator"
                          : "Imagery analyst"}
                      </p>
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        {account.email}
                      </p>
                    </button>
                  )
                })}
              </div>

              <form
                className="mt-5 rounded-[14px] border border-border bg-white p-5 sm:p-6"
                onSubmit={(event) => {
                  event.preventDefault()
                  loginMutation.mutate()
                }}
              >
                <div className="grid gap-2">
                  <Label htmlFor="email">Demo email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value)
                      loginMutation.reset()
                    }}
                    required
                    className="h-11 rounded-[10px] border-border bg-white"
                  />
                </div>
                <div className="mt-4 grid gap-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="password">Demo password</Label>
                    <span className="text-[0.68rem] text-muted-foreground">
                      Shared across both roles
                    </span>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => {
                        setPassword(event.target.value)
                        loginMutation.reset()
                      }}
                      required
                      className="h-11 rounded-[10px] border-border bg-white pr-11"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute top-1.5 right-1.5"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </Button>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between gap-3 rounded-[10px] bg-[#eef6f5] px-3 py-2.5 text-xs">
                  <span className="flex min-w-0 items-center gap-2 text-[#31584a]">
                    <KeyRound className="size-3.5 shrink-0" />
                    Password
                  </span>
                  <code className="font-semibold text-[var(--navy)]">
                    {DEMO_PASSWORD}
                  </code>
                </div>

                {loginMutation.error ? (
                  <p
                    className="mt-4 rounded-[10px] bg-[#fff0f2] px-3 py-2.5 text-sm leading-5 text-[#8f2d3a]"
                    role="alert"
                  >
                    {loginMutation.error.message}
                  </p>
                ) : null}

                <p className="mt-4 text-xs leading-5 text-muted-foreground">
                  Demo behavior: entering creates a resettable seven-day session
                  backed by synthetic records. No production identity provider
                  or external service is contacted.
                </p>
                <Button
                  type="submit"
                  size="lg"
                  className="mt-5 w-full"
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending
                    ? "Starting demo session…"
                    : "Enter selected workspace"}
                  <ArrowRight />
                </Button>
              </form>

              {viewer ? (
                <div className="mt-4 flex flex-col justify-between gap-3 rounded-[12px] border border-[#b8dbe1] bg-[#eaf7f8] px-4 py-3 sm:flex-row sm:items-center">
                  <p className="text-xs leading-5 text-[#164b59]">
                    You already have an active session as{" "}
                    <strong>{viewer.name}</strong>.
                  </p>
                  <Button
                    render={<Link to={viewerDefaultPath(viewer)} />}
                    variant="outline"
                    size="sm"
                    className="shrink-0 border-[#9ccbd4] bg-white"
                  >
                    Continue
                    <ArrowRight />
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
          <footer className="border-t border-border px-4 py-4 text-center text-xs text-muted-foreground sm:px-8">
            <span>Portfolio demo · session-scoped synthetic data · </span>
            <a
              href="https://jericop.dev"
              rel="author"
              className="font-semibold text-[var(--navy)] underline decoration-border underline-offset-4 hover:decoration-[var(--navy)]"
            >
              Built by Jerico Pulvera
            </a>
          </footer>
        </main>
      </div>
    </div>
  )
}
